import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Authentication from "../../api/Authentication/Authentication";
import "../../styles/components/user_login.css";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Create a ref to track if the component is mounted.
  const isMounted = useRef(true);

  useEffect(() => {
    // When the component mounts, isMounted is true.
    // When it unmounts, set isMounted to false.
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleUsername = (e) => setUsername(e.target.value);
  const handlePassword = (e) => setPassword(e.target.value);

  const loginClick = async () => {
    setLoading(true);
    setError("");

    try {
      const result = await Authentication.login(username, password);
      // Only update state if the component is still mounted.
      if (isMounted.current) {
        if (result.success) {
          // Check if there's a stored "from" location in state
          const from = location.state?.from?.pathname || "/";
          navigate(from, { replace: true });
        } else {
          setError("Invalid username or password.");
        }
      }
    } catch (err) {
      if (isMounted.current) {
        setError("An error occurred. Please try again.");
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };
  
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      loginClick();
    }
  };

  return (
    <div className="min-h-[100dvh] flex items-center justify-center bg-gray-900">
      <div className="bg-white/10 backdrop-blur-lg p-8 rounded-2xl shadow-lg max-w-md w-full text-white">
        <div className="flex flex-col items-center">
          <img
            src="./images/login_mech_avatar.png"
            width="60"
            height="60"
            alt="_login_avatar"
            className="rounded-full shadow-lg"
          />
          <h2 className="text-2xl font-bold mt-4">Welcome Back</h2>
        </div>

        {error && <p className="text-red-400 text-center mt-2">{error}</p>}

        <div className="mt-6">
          <input
            placeholder="Username"
            value={username}
            onChange={handleUsername}
            className="w-full p-3 rounded-lg bg-gray-800 text-white border border-gray-600 focus:border-blue-400 focus:outline-none"
            onKeyDown={handleKeyDown}
            required
          />
        </div>

        <div className="mt-4">
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={handlePassword}
            className="w-full p-3 rounded-lg bg-gray-800 text-white border border-gray-600 focus:border-blue-400 focus:outline-none"
            onKeyDown={handleKeyDown}
            required
          />
        </div>

        <div className="flex items-center justify-between mt-4">
          <label className="flex items-center">
            <input type="checkbox" className="mr-2" />
            <span className="text-sm">Remember me</span>
          </label>
        </div>

        <button
          onClick={loginClick}
          disabled={loading}
          className="mt-6 w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 rounded-lg transition duration-300 disabled:bg-gray-600"
        >
          {loading ? "Logging in..." : "Log in"}
        </button>

        <div className="mt-4 text-center">
          <span className="text-sm">Don't have an account?</span>
          <a href="/register" className="ml-1 text-blue-400 hover:underline">
            Register
          </a>
        </div>
      </div>
    </div>
  );
};

export default Login;